import { EventrDate } from '@eventr/common'
import {
    ColoursObj,
    drawerStore,
    Gutters,
    Icon,
    LoadingSpinner,
    MainButton,
    MobileDateRangePicker,
    MobileDrawerFrame,
    Typography,
    VerticalExpander,
} from '@eventr/common-storybook'
import { Box } from '@mui/material'
import { FC } from 'react'
import { useShallow } from 'zustand/react/shallow'
import i18n from '../../i18n/i18next'
import { bs, useBoundStore } from '../../state/bound.store'
import { useEphemeralStore } from '../../state/useEphemeralStore'
import { NavigationView } from '../layout/NavigationView'
import { DestinationField } from './comps/destinationField'
import { FiltersDrawer } from './filters/filtersDrawer'
import { RoomDrawer } from './filters/roomDrawer'
import { SortDrawer } from './filters/sortDrawer'
import { HotelPreviewCard } from './hotelPreviewCard'

export const HotelBookingPage: FC = () => {
    const selectedItinerary = useBoundStore().itineraryMap?.find((itin) => {
        return Number(itin.id) === bs().selectedItinID
    })
    const {
        initialSearchHotels,
        rooms,
        dateRange,
        setDateRange,
        searchResp,
        bookingLoading,
        setSelectedHotel,
        searchHotel,
        loadMoreHotels,
        hotelResp,
        destination,
    } = useEphemeralStore(
        useShallow((s) => ({
            initialSearchHotels: s.initialSearchHotels,
            rooms: s.rooms,
            dateRange: s.dateRange,
            setDateRange: s.setDateRange,
            searchResp: s.searchResp,
            bookingLoading: s.bookingLoading,
            setSelectedHotel: s.setSelectedHotel,
            searchHotel: s.searchHotel,
            loadMoreHotels: s.loadMoreHotels,
            hotelResp: s.hotelResp,
            destination: s.destination,
        }))
    )

    function getRoomsPopulation() {
        let tmpCount = 0
        rooms.forEach((room) => {
            tmpCount += room.adults + room.children.length
        })
        return tmpCount
    }

    /// str here means date not eventrdate string
    function getPrevDayStr(currentDayStr?: string) {
        const currentDay = new EventrDate(currentDayStr)
        const prevDay = currentDay.getCloneEventrDate()
        prevDay.subtract({ days: 1 })
        return prevDay.toItinLocalDateString()
    }

    function dateChange(start: string, end: string) {
        const newStartDate = new EventrDate(start)
        const newEndDate = new EventrDate(end)

        if (
            newEndDate.utcLuxon.diff(newStartDate.utcLuxon, 'days').toObject()
                .days >= 30
        ) {
            setDateRange([
                getPrevDayStr(newEndDate.dateTimeString),
                newEndDate.toItinLocalDateString(),
            ])
            return
        }

        if (
            newEndDate.utcLuxon.diff(newStartDate.utcLuxon, 'days').toObject()
                .days < 1
        ) {
            newEndDate.add({ days: 1 })
        }

        setDateRange([
            newStartDate.toItinLocalDateString(),
            newEndDate.toItinLocalDateString(),
        ])
    }

    return (
        <>
            <NavigationView
                usePageTransition
                navigationBarTitle={i18n.t('edit-itinerary.section.bookHotel')}
                navigationBarSubtitle={
                    selectedItinerary
                        ? selectedItinerary.itinerary_name ?? ''
                        : ''
                }
                backButtonAction={() => {
                    window.goBack()
                }}
            >
                <Gutters>
                    <Box display={'grid'} gap={'8px'}>
                        <Box
                            display={'grid'}
                            gridTemplateColumns={'1fr auto'}
                            gap={'8px'}
                            alignItems={'end'}
                        >
                            <DestinationField />
                            <MainButton
                                icon="Search"
                                onClick={() => {
                                    initialSearchHotels()
                                }}
                            />
                        </Box>

                        <Box>
                            <Box
                                display={'grid'}
                                gridAutoFlow={'column'}
                                gap={'8px'}
                            >
                                <MobileDateRangePicker
                                    fieldLabel={i18n.t('booking.dateRange')}
                                    startDateTimeString={
                                        dateRange && dateRange[0]
                                            ? new EventrDate(dateRange[0])
                                                  .dateTimeString
                                            : undefined
                                    }
                                    endDateTimeString={
                                        dateRange && dateRange[1]
                                            ? new EventrDate(dateRange[1])
                                                  .dateTimeString
                                            : undefined
                                    }
                                    onSave={(start, end) => {
                                        dateChange(start, end)
                                    }}
                                    openTo={
                                        dateRange && dateRange[0]
                                            ? new EventrDate(dateRange[0])
                                                  .dateTimeString
                                            : undefined
                                    }
                                    hidePast
                                />
                            </Box>
                        </Box>

                        <Box
                            display={'grid'}
                            gridTemplateColumns={'1fr 1fr'}
                            gap={'8px'}
                        >
                            <Box
                                display={'grid'}
                                gridAutoFlow={'column'}
                                gap={'8px'}
                            >
                                <MainButton
                                    variant="secondary"
                                    icon="Filter"
                                    onClick={() => {
                                        drawerStore().openDrawer({
                                            node: (
                                                <MobileDrawerFrame
                                                    title={i18n.t(
                                                        'booking.filters'
                                                    )}
                                                >
                                                    <FiltersDrawer
                                                        close={() => {
                                                            initialSearchHotels()
                                                            drawerStore().closeDrawer()
                                                        }}
                                                    />
                                                </MobileDrawerFrame>
                                            ),
                                        })
                                    }}
                                />
                                <MainButton
                                    variant="secondary"
                                    icon="Sort"
                                    onClick={() => {
                                        drawerStore().openDrawer({
                                            node: (
                                                <MobileDrawerFrame minHeight="10dvh">
                                                    <SortDrawer
                                                        close={() => {
                                                            initialSearchHotels()
                                                            drawerStore().closeDrawer()
                                                        }}
                                                    />
                                                </MobileDrawerFrame>
                                            ),
                                        })
                                    }}
                                />
                            </Box>
                            <MainButton
                                onClick={() => {
                                    drawerStore().openDrawer({
                                        node: (
                                            <MobileDrawerFrame minHeight="10dvh">
                                                <RoomDrawer
                                                    close={() => {
                                                        initialSearchHotels()
                                                        drawerStore().closeDrawer()
                                                    }}
                                                />
                                            </MobileDrawerFrame>
                                        ),
                                    })
                                }}
                            >
                                <Box
                                    display={'grid'}
                                    gap={'32px'}
                                    gridAutoFlow={'column'}
                                >
                                    <Box
                                        display={'grid'}
                                        gap={'4px'}
                                        gridAutoFlow={'column'}
                                    >
                                        <Icon
                                            name="Room_Leave"
                                            iconColour={
                                                ColoursObj.shades.shade5
                                            }
                                        />
                                        <Typography
                                            color={ColoursObj.shades.shade5}
                                        >
                                            {rooms && rooms.length}
                                        </Typography>
                                    </Box>
                                    <Box
                                        display={'grid'}
                                        gap={'4px'}
                                        gridAutoFlow={'column'}
                                    >
                                        <Icon
                                            name="Admin"
                                            iconColour={
                                                ColoursObj.shades.shade5
                                            }
                                        />
                                        <Typography
                                            color={ColoursObj.shades.shade5}
                                        >
                                            {rooms && getRoomsPopulation()}
                                        </Typography>
                                    </Box>
                                </Box>
                            </MainButton>
                        </Box>
                        {/* Main Hotel List */}
                        {hotelResp &&
                            hotelResp.hotelInfo &&
                            destination?.obj?.grType === 'Hotels' && (
                                <Box
                                    onClick={() => {
                                        setSelectedHotel(
                                            hotelResp.hotelInfo.providerHotelId,
                                            hotelResp.hotelInfo.provider
                                        )
                                        searchHotel()
                                        window.pushPath(
                                            '/edit/hotelbooking/details'
                                        )
                                    }}
                                >
                                    <HotelPreviewCard
                                        hotelData={hotelResp.hotelInfo}
                                    />
                                </Box>
                            )}
                        {searchResp && searchResp.hotels && (
                            <Box display={'grid'} gap={'8px'}>
                                {searchResp.hotels.map((hotelData, index) => {
                                    return (
                                        <Box
                                            key={
                                                index +
                                                hotelData.providerHotelId
                                            }
                                            onClick={() => {
                                                setSelectedHotel(
                                                    hotelData.providerHotelId,
                                                    hotelData.provider
                                                )
                                                searchHotel()
                                                window.pushPath(
                                                    '/edit/hotelbooking/details'
                                                )
                                            }}
                                        >
                                            <HotelPreviewCard
                                                hotelData={hotelData.hotelInfo}
                                                rate={hotelData.rate}
                                            />
                                        </Box>
                                    )
                                })}

                                {searchResp.hotels.length > 0 ? (
                                    <Box display={'grid'}>
                                        <MainButton
                                            text={i18n.t('booking.loadMore')}
                                            onClick={loadMoreHotels}
                                        />
                                    </Box>
                                ) : (
                                    <Box
                                        display={'grid'}
                                        justifyItems={'center'}
                                        padding={'16px 0 0 0'}
                                    >
                                        <Typography variant="bodyBold">
                                            {i18n.t('booking.noHotels')}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        )}

                        <VerticalExpander open={bookingLoading}>
                            <Box padding={'16px'}>
                                <LoadingSpinner />
                            </Box>
                        </VerticalExpander>
                        <Box height={'32px'} />
                    </Box>
                </Gutters>
            </NavigationView>
        </>
    )
}
